import { useState, useEffect, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import { FiSend, FiUsers, FiMessageCircle, FiRefreshCw } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

export default function Chat() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000); // 30초마다 새로고침
    return () => clearInterval(interval);
  }, []);

  const loadMessages = useCallback(async (userId) => {
    setLoadingMessages(true);
    try {
      const res = await api.get(`/api/chat/messages/${userId}`);
      setMessages(res.data.messages || []);

      // Mark as read
      await api.put(`/api/chat/read/${userId}`);
      loadData();
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (selectedUser) {
      loadMessages(selectedUser._id);
    }
  }, [selectedUser, loadMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadData = async () => {
    try {
      const [usersRes, convRes, unreadRes] = await Promise.all([
        api.get('/api/chat/users'),
        api.get('/api/chat/conversations'),
        api.get('/api/chat/unread-count')
      ]);
      setUsers(usersRes.data.users || []);
      setConversations(convRes.data.conversations || []);
      setUnreadCount(unreadRes.data.count || 0);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const selectUser = (u) => {
    setSelectedUser(u);
  };

  const selectConversation = (conv) => {
    const u = users.find(u => u._id === conv._id) || {
      _id: conv._id,
      name: conv.partnerName,
      role: conv.partnerRole
    };
    setSelectedUser(u);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedUser) return;

    try {
      await api.post('/api/chat/send', {
        recipientId: selectedUser._id,
        message: message.trim()
      });
      setMessage('');
      loadMessages(selectedUser._id);
    } catch (error) {
      alert(error.response?.data?.error || '메시지 전송에 실패했습니다');
    }
  };

  const getRoleLabel = (role) => {
    return role === 'admin' ? '관리자' : '강사';
  };

  const isMyMessage = (msg) => {
    return msg.senderId?.toString() === user?._id?.toString() ||
           msg.senderId === user?._id;
  };

  return (
    <div className="chat-page">
      <div className="page-header">
        <h2>
          <FiMessageCircle /> 채팅
          {unreadCount > 0 && <span className="badge badge-primary">{unreadCount}</span>}
        </h2>
        <button className="btn btn-secondary" onClick={loadData}>
          <FiRefreshCw /> 새로고침
        </button>
      </div>

      <div className="chat-container">
        {/* Sidebar - Users & Conversations */}
        <div className="chat-sidebar">
          {/* Recent Conversations */}
          {conversations.length > 0 && (
            <div className="sidebar-section">
              <h4>최근 대화</h4>
              <div className="conversation-list">
                {conversations.map((conv) => (
                  <div
                    key={conv._id}
                    className={`conversation-item ${selectedUser?._id === conv._id ? 'active' : ''} ${conv.unreadCount > 0 ? 'unread' : ''}`}
                    onClick={() => selectConversation(conv)}
                  >
                    <div className="conv-avatar">
                      {conv.partnerName?.[0] || '?'}
                    </div>
                    <div className="conv-info">
                      <div className="conv-header">
                        <span className="conv-name">{conv.partnerName}</span>
                        <span className="conv-time">
                          {format(new Date(conv.lastMessageTime), 'MM/dd HH:mm')}
                        </span>
                      </div>
                      <div className="conv-preview">
                        <span className="conv-message">{conv.lastMessage}</span>
                        {conv.unreadCount > 0 && (
                          <span className="unread-badge">{conv.unreadCount}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Users */}
          <div className="sidebar-section">
            <h4>전체 사용자</h4>
            <div className="user-list">
              {loading ? (
                <div className="loading">로딩 중...</div>
              ) : users.length === 0 ? (
                <div className="empty">채팅 가능한 사용자가 없습니다</div>
              ) : (
                users.map((u) => (
                  <div
                    key={u._id}
                    className={`user-item ${selectedUser?._id === u._id ? 'active' : ''}`}
                    onClick={() => selectUser(u)}
                  >
                    <div className="user-avatar">
                      {u.name?.[0] || '?'}
                    </div>
                    <div className="user-info">
                      <span className="user-name">{u.name}</span>
                      <span className={`user-role ${u.role}`}>
                        {getRoleLabel(u.role)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className="chat-main">
          {selectedUser ? (
            <>
              {/* Chat Header */}
              <div className="chat-header">
                <div className="chat-user">
                  <div className="user-avatar">
                    {selectedUser.name?.[0] || '?'}
                  </div>
                  <div className="user-info">
                    <span className="user-name">{selectedUser.name}</span>
                    <span className={`user-role ${selectedUser.role}`}>
                      {getRoleLabel(selectedUser.role)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="chat-messages">
                {loadingMessages ? (
                  <div className="loading">메시지 로딩 중...</div>
                ) : messages.length === 0 ? (
                  <div className="empty-messages">
                    <FiMessageCircle size={48} />
                    <p>메시지가 없습니다</p>
                    <p>첫 메시지를 보내보세요!</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg._id}
                      className={`message ${isMyMessage(msg) ? 'mine' : 'theirs'}`}
                    >
                      <div className="message-content">
                        <p>{msg.message}</p>
                        <span className="message-time">
                          {format(new Date(msg.createdAt), 'HH:mm')}
                          {isMyMessage(msg) && msg.isRead && (
                            <span className="read-indicator"> 읽음</span>
                          )}
                        </span>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form className="chat-input" onSubmit={handleSend}>
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="메시지를 입력하세요..."
                  autoFocus
                />
                <button type="submit" className="btn btn-primary" disabled={!message.trim()}>
                  <FiSend />
                </button>
              </form>
            </>
          ) : (
            <div className="chat-placeholder">
              <FiUsers size={64} />
              <h3>대화 상대를 선택하세요</h3>
              <p>왼쪽에서 대화하고 싶은 사람을 선택하세요</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
